import React, { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { collect } from 'collect.js';
import classnames from 'classnames';
import OrderLineItem from '../../../components/cards/OrderLineItem';
import {
  CouponType,
  DeliveryProvider,
  OrderFulfillmentStatus,
  OrderMethod,
  PaymentMethod,
  TaxBehavior,
} from '../../../services/exports/Constants';
import useFeedbackHandler from '../../../hooks/useFeedbackHandler';
import usePrinter from '../../../hooks/usePrinter';
import RefundIcon from '../../../resources/assets/icons/RefundIcon';
import CreateRefundModal from '../CreateRefundModal';
import Footer from './footers/Footer';
import CarrierIcon from '../../../resources/assets/icons/CarrierIcon';
import PhoneIcon from '../../../resources/assets/icons/PhoneIcon';
import PrimaryOrderBadge from '../../../components/badge/PrimaryOrderBadge';
import OrderMethodIcon from '../../../components/order/OrderMethodIcon';
import { ReceiptType } from '../../../providers/PrintProvider';
import { useOngoingOrders } from '../../../providers/OngoingOrdersProvider';
import { useHelpers } from '../../../providers/HelpersProvider';
import { Alert, Button } from 'antd';
import { CreditCardOutlined, EllipsisOutlined, PrinterOutlined, StarOutlined, WalletOutlined } from '@ant-design/icons';
import Drawer from '../../../components/lib/Drawer';
import useScreenType from '../../../hooks/utility/useScreenType';
import OrderHelpers from '../../../services/helpers/OrderHelpers';
import AddressHelpers from '../../../services/helpers/AddressHelpers';
import { DateTime } from 'luxon';

const Divider = ({ children }) => <div className="bg-gray-100 py-xs px-md w-full font-semibold">{children}</div>;

const Schedule = ({ isReminder, className, children }) => (
  <div
    className={classnames(
      'p-md relative',
      {
        'bg-orange-500': isReminder,
        'bg-blue-500': !isReminder,
      },
      className,
    )}
  >
    {children}
  </div>
);

const OrderDetailsModal = () => {
  const { t } = useTranslation(null, { keyPrefix: 'Modals:Order:OrderDetails:OrderDetailsModal' });

  const { isMobile } = useScreenType();
  const { selectedOrder: order, toggleOrderDetailsModal, toggleRejectOrderModal } = useOngoingOrders();
  const { print, printing } = usePrinter();
  const { formatCurrency, formatTime, formatDateTime } = useHelpers();

  const company = useSelector((state) => state.company?.company);
  const { payment } = order;

  const [showRefundModal, setShowRefundModal] = useState(false);
  const { setFeedback } = useFeedbackHandler({ renderToasts: true });

  const toggleRefundModal = () => setShowRefundModal((current) => !current);

  const [showOptionsSideBar, setShowOptionsSidebar] = useState(false);

  const isRejectable =
    order.fulfillment_status === OrderFulfillmentStatus.Created ||
    order.fulfillment_status === OrderFulfillmentStatus.Reviewed ||
    order.fulfillment_status === OrderFulfillmentStatus.Accepted ||
    order.fulfillment_status === OrderFulfillmentStatus.Preparing ||
    order.fulfillment_status === OrderFulfillmentStatus.Ready ||
    order.fulfillment_status === OrderFulfillmentStatus.OnDelivery;

  const isRefundable =
    order.fulfillment_status !== OrderFulfillmentStatus.Rejected &&
    payment &&
    // if payment has frozen amount we dont allow to refund it, but we still allow to open refund modal with more info
    (payment.frozen_amount !== 0 || payment.total_amount - collect(payment.adjustments).sum('total_amount') !== 0);

  function onError(error) {
    setFeedback({
      type: 'error',
      message: error,
    });
  }

  const renderOrderMethodLabel = () =>
    ({
      [OrderMethod.OnSite]: t(`order_methods.${order.room_service_location?.type}`),
      [OrderMethod.Pickup]: t(`order_methods.${order.method}`),
      [OrderMethod.Delivery]: t(`order_methods.${order.method}`),
    })[order.method];

  const renderScheduledDate = () => {
    if (!order.scheduled_for) {
      return null;
    }

    return (
      <span className="mb-2xs text-center">
        {DateTime.fromISO(order.scheduled_for).hasSame(DateTime.local(), 'day')
          ? t('labels.today').capitalize()
          : DateTime.fromISO(order.scheduled_for).toFormat('cccc, dd LLL')}
        <br />
      </span>
    );
  };

  const toggleOptionsSidebar = () => {
    setShowOptionsSidebar(!showOptionsSideBar);
  };

  return (
    <Drawer open title={t('title')} push={{ distance: 0 }} onClose={() => toggleOrderDetailsModal()}>
      <div className="h-full overflow-y-auto">
        <OrderDetailsModal.Section className="space-y-none flex w-full justify-between gap-4">
          <div className="space-y-xs">
            <h5 className={classnames('flex', { 'text-danger': order.is_dummy })}>
              {order.is_dummy ? t('labels.dummy_order') : `#${order.number}`}
            </h5>
            <p className="font-medium">
              {order.customer_first_name} {order.customer_last_name}
            </p>
            {order.method === OrderMethod.Delivery && (
              <>
                {order.delivery_provider === DeliveryProvider.WoltDrive && (
                  <>
                    <p className="text-wolt">
                      {t('labels.wolt_support', { phone_number: import.meta.env.VITE_WOLT_SUPPORT_PHONE_NUMBER })}
                    </p>
                    <Alert type="warning" title={<Trans t={t}>labels.wolt_support_text</Trans>} className="mt-xs" />
                  </>
                )}
                {AddressHelpers.lines(
                  {
                    street_name: order.delivery_street_name,
                    street_number: order.delivery_street_number,
                    zip_code: order.delivery_zip_code,
                    city: order.delivery_city,
                  },
                  company?.region?.address_format,
                ).map((line) => (
                  <p key={line}>{line}</p>
                ))}
                {order.delivery_doorbell_name && <p>{order.delivery_doorbell_name}</p>}
                {order.delivery_company_name && <p>{order.delivery_company_name}</p>}
              </>
            )}
            {order.method === OrderMethod.OnSite && (
              <>
                <p>{order.room_service_location.name}</p>
                <p>{AddressHelpers.streetLine(order.room_service_location, company?.region?.address_format)}</p>
                {order.room_service_details && (
                  <p>
                    {t(`room_service_location_types.${order.room_service_location.type}.details_label`)}
                    {': '}
                    {order.room_service_details}
                  </p>
                )}
              </>
            )}
            <p>
              <b>Tel:</b> {order.customer_phone_number}
            </p>
            {order.order_instructions?.length > 0 && (
              <p>
                <b>{t('labels.order_instructions')}</b> {order.order_instructions}
              </p>
            )}
            <Button
              loading={printing}
              variant="filled"
              color="default"
              className="mt-md"
              icon={<PrinterOutlined />}
              onClick={() => print({ type: ReceiptType.Order, payload: order })}
            >
              {t('buttons.print')}
            </Button>
          </div>
          <div className="space-y-xs">
            {(isRejectable || isRefundable) && (
              <div className="flex justify-center">
                <Button type="text" className="text-2xl" icon={<EllipsisOutlined />} onClick={toggleOptionsSidebar} />
              </div>
            )}
            <div className="flex">
              <OrderMethodIcon order={order} className="mx-auto" />
            </div>
            <span className="text-sm text-gray-500 block w-full text-center">{renderOrderMethodLabel()}</span>
            <PrimaryOrderBadge order={order} className="mx-auto">
              {renderScheduledDate()}
              <span className="block text-center">
                {order.scheduled_for || OrderHelpers.isExternalDelivery(order)
                  ? formatTime(order.estimated_pickup_time)
                  : t('labels.asap')}
              </span>
            </PrimaryOrderBadge>
          </div>
        </OrderDetailsModal.Section>

        {order.coupon_type === CouponType.Reward && (
          <OrderDetailsModal.Section>
            <Alert icon={<StarOutlined />} showIcon type="warning" title={t('labels.points_used')} />
          </OrderDetailsModal.Section>
        )}
        {order.scheduled_for && order.fulfillment_status !== OrderFulfillmentStatus.Preparing && (
          <Schedule isReminder={order.fulfillment_status === OrderFulfillmentStatus.Accepted} className="mb-md">
            <h5 className="text-white text-center">
              {t('schedule.header', {
                date: DateTime.fromISO(order.estimated_pickup_time).hasSame(DateTime.local(), 'day')
                  ? t('labels.today')
                  : DateTime.fromISO(order.estimated_pickup_time).toFormat('cccc, dd LLL'),
                time: formatTime(order.estimated_pickup_time),
              })}
            </h5>
            <span className="block text-center text-white break-words">{t('schedule.text')}</span>
          </Schedule>
        )}
        {Boolean(order.delivery?.runner_name) && (
          <>
            <Divider>{t('sections.driver_info')}</Divider>
            <OrderDetailsModal.Section>
              <p>
                <CarrierIcon className="mr-xs" />
                <b>{t('labels.driver_name')}</b>
                {': '}
                {order.delivery?.runner_name}
              </p>
              {Boolean(order.delivery?.runner_phone_number) && (
                <p>
                  <PhoneIcon className="mr-xs" />
                  <b>{t('labels.driver_phone_number')}</b>
                  {': '}
                  {order.delivery?.runner_phone_number}
                  {Boolean(order?.delivery?.runner_phone_pin_code) && (
                    <>
                      {' ('}
                      <b>{t('labels.pin_code')}</b>
                      {': '}
                      {order?.delivery?.runner_phone_pin_code}
                      {')'}
                    </>
                  )}
                </p>
              )}
              {Boolean(order.delivery?.pin_code) && (
                <p>
                  <b>{t('labels.dropoff_pin_code')}</b>
                  {': '}
                  {order.delivery?.pin_code}
                </p>
              )}
            </OrderDetailsModal.Section>
          </>
        )}
        <Divider>{t('sections.order_info')}</Divider>
        <OrderDetailsModal.Section>
          {order.line_items.map((item, idx) => (
            <OrderLineItem lineItem={item} isLastItem={idx === order.line_items.length - 1} key={idx} />
          ))}
        </OrderDetailsModal.Section>
        <Divider>{t('sections.price_info')}</Divider>
        <OrderDetailsModal.Section>
          <div className="flex justify-between">
            <span>{t('labels.original')}</span>
            <span>{formatCurrency(order.original_price)}</span>
          </div>
          {order.method_fee > 0 ? (
            <div className="flex justify-between">
              <span>{t('labels.delivery_fee')}</span>
              <span>{formatCurrency(order.method_fee)}</span>
            </div>
          ) : undefined}
          {order.tip > 0 && (
            <div className="flex justify-between">
              <span>{t('labels.tip')}</span>
              <span>{formatCurrency(order.tip)}</span>
            </div>
          )}
          {order.service_fee > 0 && (
            <div className="flex justify-between">
              <span>{t('labels.service_fee')}</span>
              <span>{formatCurrency(order.service_fee)}</span>
            </div>
          )}
          {order.payment_method_upcharge_fee > 0 && (
            <div className="flex justify-between">
              <span>{t(`labels.payment_method_upcharge_fee.${payment.payment_method_id}`)}</span>
              <span>{formatCurrency(order.payment_method_upcharge_fee)}</span>
            </div>
          )}
          {order.total_discount > 0 && <DiscountBreakdown data={order} />}
          {order.tax_behaviour === TaxBehavior.Exclusive && order.tax > 0 && (
            <div className="flex justify-between">
              <span>{t('labels.tax')}</span>
              <span>{formatCurrency(order.tax)}</span>
            </div>
          )}
          <div className="flex justify-between font-semibold">
            <span>{t('labels.total')}</span>
            <span>{formatCurrency(order.total_amount)}</span>
          </div>
          {Boolean(payment) && (
            <div className="text-sm flex gap-xs">
              <span className="text-md">
                {payment.payment_method_id === PaymentMethod.Cash ? <WalletOutlined /> : <CreditCardOutlined />}
              </span>
              <span className="my-auto">
                {payment.payment_method_id === PaymentMethod.Cash
                  ? t(company?.overwrite_cash_payments_to_invoice ? 'labels.invoice_payment' : 'labels.cash_payment')
                  : t('labels.online_payment', { provider: company?.service_provider?.name })}
              </span>
            </div>
          )}
        </OrderDetailsModal.Section>
        {Boolean(payment) && payment.adjustments?.length > 0 && (
          <>
            <Divider>{t('sections.adjustments')}</Divider>
            <OrderDetailsModal.Section>
              {payment.adjustments.map((item) => (
                <div key={`adjustment-${item.id}`}>
                  <div className="flex justify-between font-medium">
                    <span>{formatDateTime(item?.paid_at)}</span>
                    <span>
                      {item.total_amount > 0 ? '+' : '-'} {formatCurrency(Math.abs(item.total_amount))}
                    </span>
                  </div>
                  <span className="text-sm underline">
                    {t(`adjustment_reasons.${item.adjustment_reason}`, item.adjustment_reason)}
                  </span>
                </div>
              ))}
            </OrderDetailsModal.Section>
          </>
        )}
      </div>
      <Footer order={order} onError={onError} printing={printing} />
      <Drawer
        hideCloseIcon
        open={showOptionsSideBar}
        size={isMobile ? '70%' : undefined}
        onClose={() => setShowOptionsSidebar(false)}
      >
        {isRefundable && (
          <Button
            type="text"
            onClick={() => {
              toggleOptionsSidebar();
              toggleRefundModal();
            }}
            className="py-sm h-fit justify-start border-b border-gray-200 rounded-none"
          >
            <RefundIcon className="mr-xs my-auto" />
            {t('buttons.refund')}
          </Button>
        )}
        {isRejectable && (
          <Button
            type="text"
            onClick={() => {
              toggleOptionsSidebar();
              toggleRejectOrderModal(order);
            }}
            className="py-sm h-fit justify-start text-danger hover:!text-danger rounded-none"
          >
            {t('buttons.reject')}
          </Button>
        )}
      </Drawer>
      {showRefundModal && <CreateRefundModal payment={payment} toggleModal={toggleRefundModal} />}
    </Drawer>
  );
};

export default OrderDetailsModal;

const DiscountBreakdown = ({ data }) => {
  const { t: translate } = useTranslation(null, {
    keyPrefix: 'Components:Print:InnerPrinterReceipt.discount_breakdown',
  });
  const company = useSelector((state) => state.company.company);
  const lng = company?.legal_lang ?? company?.region?.default_locale;
  const { formatCurrency } = useHelpers();

  if (!data?.discount_breakdown) {
    return null;
  }

  const entries = Object.entries(data?.discount_breakdown);

  if (!entries?.length) {
    return null;
  }

  function t(key, config) {
    return translate(key, { lng, ...config });
  }

  return (
    <>
      <div className="flex justify-between">
        <span>{t('label')}</span>
        <span>-{formatCurrency(data.total_discount)}</span>
      </div>
      {entries.map((entry) => (
        <div className="flex justify-between ml-md" key={entry[0]}>
          <span>{t(entry[0])}</span>
          <span>-{formatCurrency(entry[1] / 100)}</span>
        </div>
      ))}
    </>
  );
};

OrderDetailsModal.Section = ({ className, children }) => (
  <div className={classnames('p-md space-y-md', className)}>{children}</div>
);
